// Per-dialect synthesizer for partitioning ops (v2 Slice E1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice E1.
//
// Per-engine surface (heaviest slice in v2):
//   - mssql: CREATE PARTITION FUNCTION / SCHEME + ALTER TABLE … SWITCH
//            PARTITION. mssql does not support adding/dropping partitions on
//            an existing table directly (uses scheme rebinding).
//   - pgsql: ATTACH / DETACH PARTITION on an existing partitioned parent.
//   - mysql: ALTER TABLE … PARTITION BY <spec> (promote-to-partitioned),
//            ALTER TABLE … ADD/DROP PARTITION on existing partitioned tables.
//   - oracle: ALTER TABLE … MODIFY PARTITION BY <spec> (12.2+),
//             ALTER TABLE … ADD/DROP PARTITION.
//   - sqlite: not supported.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type {
  PartitionBoundary,
  PartitionDef,
  PartitionFunctionDef,
  PartitionSchemeDef,
  PartitioningSpec,
} from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

function unsupported(engine: SqlEngine, op: string, alternative: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${alternative}`,
  );
}

// ─── PartitioningSpec → text ──────────────────────────────────────────────

function specColumnsText(columns: readonly string[]): string {
  if (columns.length === 0) {
    throw new Error(`applyDdlEdit: partitioning spec requires at least one column`);
  }
  return columns.join(', ');
}

function partitionBoundaryText(
  engine: SqlEngine,
  boundary: PartitionBoundary,
): string {
  switch (boundary.kind) {
    case 'range-less-than':
      return `VALUES LESS THAN (${boundary.valuesText})`;
    case 'range-from-to':
      if (engine !== 'postgres') {
        throw new Error(
          `applyDdlEdit: ${engine} does not accept range 'from-to' partition `
            + `boundaries — only pgsql supports FOR VALUES FROM (…) TO (…). `
            + `Use 'range-less-than' on mysql / oracle.`,
        );
      }
      return `FOR VALUES FROM (${boundary.fromText}) TO (${boundary.toText})`;
    case 'list-in':
      if (engine === 'postgres') return `FOR VALUES IN (${boundary.valuesText})`;
      return `VALUES (${boundary.valuesText})`;
    case 'list-default':
      if (engine !== 'postgres') {
        throw new Error(
          `applyDdlEdit: ${engine} does not accept 'list-default' partition `
            + `boundary — only pgsql supports FOR VALUES DEFAULT.`,
        );
      }
      return 'DEFAULT';
    case 'maxvalue':
      return 'VALUES LESS THAN (MAXVALUE)';
    case 'hash':
    case 'system':
      return '';
  }
}

function partitionInlineText(
  engine: SqlEngine,
  partition: PartitionDef,
): string {
  let s = `PARTITION ${maybeQuoteIdent(engine, partition.name, true)}`;
  const boundary = partitionBoundaryText(engine, partition.boundary);
  if (boundary.length > 0) s += ` ${boundary}`;
  if (partition.tablespace) s += ` TABLESPACE ${partition.tablespace}`;
  if (partition.subpartitionsText) s += ` (${partition.subpartitionsText})`;
  return s;
}

function partitionListText(
  engine: SqlEngine,
  partitions: readonly PartitionDef[] | undefined,
): string {
  if (!partitions || partitions.length === 0) return '';
  const parts = partitions.map((p) => partitionInlineText(engine, p));
  return ` (${parts.join(', ')})`;
}

/** Format a `PartitioningSpec` as the engine's `PARTITION BY …` clause text
 *  (without the leading `PARTITION BY` / `MODIFY PARTITION BY` keyword — the
 *  caller wraps that). Engine-specific shape lives here. */
export function formatPartitioningSpec(
  engine: SqlEngine,
  spec: PartitioningSpec,
): string {
  switch (spec.kind) {
    case 'range': {
      const cols = specColumnsText(spec.columns);
      const head = engine === 'mysql' && spec.mysqlColumns ? `RANGE COLUMNS(${cols})` : `RANGE (${cols})`;
      return `${head}${partitionListText(engine, spec.partitions)}`;
    }
    case 'list': {
      const cols = specColumnsText(spec.columns);
      const head = engine === 'mysql' && spec.mysqlColumns ? `LIST COLUMNS(${cols})` : `LIST (${cols})`;
      return `${head}${partitionListText(engine, spec.partitions)}`;
    }
    case 'hash': {
      const cols = specColumnsText(spec.columns);
      let s = `HASH (${cols})`;
      if (spec.partitions !== undefined) s += ` PARTITIONS ${spec.partitions}`;
      s += partitionListText(engine, spec.partitionList);
      return s;
    }
    case 'key': {
      if (engine !== 'mysql') {
        throw new DdlEditError(
          'engine-does-not-support-op',
          `applyDdlEdit: ${engine} does not support KEY partitioning — `
            + `mysql is the only engine with PARTITION BY KEY. Use HASH on `
            + `pgsql / oracle.`,
        );
      }
      const cols = specColumnsText(spec.columns);
      const head = spec.linear ? `LINEAR KEY (${cols})` : `KEY (${cols})`;
      let s = head;
      if (spec.partitions !== undefined) s += ` PARTITIONS ${spec.partitions}`;
      return s;
    }
    case 'reference': {
      if (engine !== 'oracle') {
        throw new DdlEditError(
          'engine-does-not-support-op',
          `applyDdlEdit: ${engine} does not support REFERENCE partitioning — `
            + `oracle is the only engine with PARTITION BY REFERENCE.`,
        );
      }
      return `REFERENCE (${spec.refConstraint})`;
    }
    case 'system': {
      if (engine !== 'oracle') {
        throw new DdlEditError(
          'engine-does-not-support-op',
          `applyDdlEdit: ${engine} does not support SYSTEM partitioning — `
            + `oracle is the only engine with PARTITION BY SYSTEM.`,
        );
      }
      let s = 'SYSTEM';
      if (spec.partitions !== undefined) s += ` PARTITIONS ${spec.partitions}`;
      return s;
    }
  }
}

// ─── partition-table ──────────────────────────────────────────────────────

export function synthesizePartitionTable(
  engine: SqlEngine,
  table: QualifiedName,
  spec: PartitioningSpec,
): string {
  if (engine === 'postgres') {
    unsupported(
      engine,
      'partition-table',
      `pgsql partitioning must be declared at CREATE TABLE time. Recreate the `
        + `table with PARTITION BY <spec>, or use a partitioned-by-design parent `
        + `with ATTACH PARTITION (op:'add-partition').`,
    );
  }
  if (engine === 'mssql') {
    unsupported(
      engine,
      'partition-table',
      `mssql binds partitioning through a partition scheme at CREATE TABLE / `
        + `CREATE INDEX time. Use op:'create-partition-function' + `
        + `op:'create-partition-scheme' and rebuild the table on the scheme.`,
    );
  }
  if (engine === 'sqlite') {
    unsupported(engine, 'partition-table', `sqlite has no partitioning support.`);
  }
  const specText = formatPartitioningSpec(engine, spec);
  if (engine === 'mysql') {
    return `ALTER TABLE ${tableText(engine, table)} PARTITION BY ${specText}`;
  }
  // oracle 12.2+
  return `ALTER TABLE ${tableText(engine, table)} MODIFY PARTITION BY ${specText}`;
}

// ─── add-partition ────────────────────────────────────────────────────────

export function synthesizeAddPartition(
  engine: SqlEngine,
  table: QualifiedName,
  partition: PartitionDef,
): string {
  if (engine === 'mssql') {
    unsupported(
      engine,
      'add-partition',
      `mssql adds partitions by altering the partition function — use `
        + `ALTER PARTITION FUNCTION pf SPLIT RANGE / MERGE RANGE on the bound `
        + `function (out of v2 scope; emit directly via emitSource).`,
    );
  }
  if (engine === 'sqlite') {
    unsupported(engine, 'add-partition', `sqlite has no partitioning support.`);
  }
  if (engine === 'postgres') {
    const boundary = partitionBoundaryText(engine, partition.boundary);
    if (boundary.length === 0) {
      throw new Error(
        `applyDdlEdit: pgsql add-partition requires a boundary (FOR VALUES …) — `
          + `'hash' / 'system' boundaries are not valid for ATTACH PARTITION.`,
      );
    }
    return `ALTER TABLE ${tableText(engine, table)} ATTACH PARTITION ${maybeQuoteIdent(engine, partition.name, true)} ${boundary}`;
  }
  // mysql + oracle
  const inline = partitionInlineText(engine, partition);
  if (engine === 'mysql') {
    return `ALTER TABLE ${tableText(engine, table)} ADD PARTITION (${inline})`;
  }
  // oracle
  return `ALTER TABLE ${tableText(engine, table)} ADD ${inline}`;
}

// ─── drop-partition ───────────────────────────────────────────────────────

export function synthesizeDropPartition(
  engine: SqlEngine,
  table: QualifiedName,
  partitionName: string,
  pgsqlConcurrently: boolean,
  oracleUpdateGlobalIndexes: boolean,
): string {
  if (engine === 'mssql') {
    unsupported(
      engine,
      'drop-partition',
      `mssql drops partitions via ALTER PARTITION FUNCTION pf MERGE RANGE — `
        + `out of v2 scope; emit directly via emitSource.`,
    );
  }
  if (engine === 'sqlite') {
    unsupported(engine, 'drop-partition', `sqlite has no partitioning support.`);
  }
  if (pgsqlConcurrently && engine !== 'postgres') {
    throw new Error(
      `applyDdlEdit: ${engine} drop-partition does not accept 'pgsqlConcurrently' `
        + `— only pgsql supports DETACH PARTITION CONCURRENTLY.`,
    );
  }
  if (oracleUpdateGlobalIndexes && engine !== 'oracle') {
    throw new Error(
      `applyDdlEdit: ${engine} drop-partition does not accept `
        + `'oracleUpdateGlobalIndexes' — only oracle supports the UPDATE GLOBAL `
        + `INDEXES clause.`,
    );
  }
  const partitionIdent = maybeQuoteIdent(engine, partitionName, true);
  if (engine === 'postgres') {
    let s = `ALTER TABLE ${tableText(engine, table)} DETACH PARTITION ${partitionIdent}`;
    if (pgsqlConcurrently) s += ' CONCURRENTLY';
    return s;
  }
  if (engine === 'mysql') {
    return `ALTER TABLE ${tableText(engine, table)} DROP PARTITION ${partitionIdent}`;
  }
  // oracle
  let s = `ALTER TABLE ${tableText(engine, table)} DROP PARTITION ${partitionIdent}`;
  if (oracleUpdateGlobalIndexes) s += ' UPDATE GLOBAL INDEXES';
  return s;
}

// ─── create-partition-function (mssql-only) ───────────────────────────────

export function synthesizeCreatePartitionFunction(
  engine: SqlEngine,
  pf: PartitionFunctionDef,
): string {
  if (engine !== 'mssql') {
    unsupported(
      engine,
      'create-partition-function',
      `partition functions are an mssql-only construct; other engines bake `
        + `partitioning into the table — use op:'partition-table' instead.`,
    );
  }
  if (pf.boundaryValues.length === 0) {
    throw new Error(
      `applyDdlEdit: mssql create-partition-function requires at least one `
        + `boundary value`,
    );
  }
  const dir = pf.rangeDirection.toUpperCase();
  return `CREATE PARTITION FUNCTION ${maybeQuoteIdent(engine, pf.name, true)} (${pf.parameterType}) AS RANGE ${dir} `
    + `FOR VALUES (${pf.boundaryValues.join(', ')})`;
}

// ─── create-partition-scheme (mssql-only) ─────────────────────────────────

export function synthesizeCreatePartitionScheme(
  engine: SqlEngine,
  ps: PartitionSchemeDef,
): string {
  if (engine !== 'mssql') {
    unsupported(
      engine,
      'create-partition-scheme',
      `partition schemes are an mssql-only construct.`,
    );
  }
  let s = `CREATE PARTITION SCHEME ${maybeQuoteIdent(engine, ps.name, true)} `
    + `AS PARTITION ${maybeQuoteIdent(engine, ps.partitionFunction, true)}`;
  if (ps.filegroups.mode === 'all') {
    s += ` ALL TO ([${ps.filegroups.filegroup}])`;
  } else {
    if (ps.filegroups.filegroups.length === 0) {
      throw new Error(
        `applyDdlEdit: mssql create-partition-scheme 'list' mode requires at `
          + `least one filegroup`,
      );
    }
    const fgs = ps.filegroups.filegroups.map((fg) => `[${fg}]`).join(', ');
    s += ` TO (${fgs})`;
  }
  return s;
}

// ─── switch-partition (mssql-only) ────────────────────────────────────────

export function synthesizeSwitchPartition(
  engine: SqlEngine,
  sourceTable: QualifiedName,
  sourcePartition: number,
  targetTable: QualifiedName,
  targetPartition: number,
): string {
  if (engine !== 'mssql') {
    unsupported(
      engine,
      'switch-partition',
      `partition SWITCH is mssql-only — use mysql 'EXCHANGE PARTITION' / `
        + `oracle 'EXCHANGE PARTITION' / pgsql DETACH+ATTACH (out of v2 scope).`,
    );
  }
  return `ALTER TABLE ${tableText(engine, sourceTable)} SWITCH PARTITION `
    + `${sourcePartition} TO ${tableText(engine, targetTable)} PARTITION ${targetPartition}`;
}
